import React, { useCallback, useEffect, useState } from "react";
import { Form, Layout, Button, Collapse, Typography, message } from "antd";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { getMovementType } from "../../../common/helper/helper";
import InventoryLayOutCommon from "../LayOut/InventoryLayOutCommon";
import AppPageHeader from "../../../common/PageHeader";
import InventoryForm from "./InventoryForm";
import ItemTable from "../ItemTable";
import {
  onGetBOMDetails,
  onGetBuyingOfficeDetails,
  onGetBuyingOfficeList,
  onGetStyleDetails,
  onGetStyleList,
  onResetStyleDetails,
  onResetBOM,
} from "../../../redux/actions";
import { onCreateInventory } from "../../../redux/actions";
import HandleNotification from "../../../common/Notification";
import { CHALLAN_LIST, INVOICE_BY_SUPPLIER } from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";

const CreateFabricInventory = (props) => {
  // States
  const [value, setValue] = useState(null);
  const [originalPO, setOriginalPO] = useState("");
  const [internalPO, setInternalPO] = useState("");
  const [inventoryItems, setInventoryItems] = useState([]);
  const [onEdit, setEdit] = useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [invoiceList, setInvoiceList] = useState(null);
  const [challanList, setChallanList] = useState(null);
  const [selectedSupplierType, setSelectedSupplierType] = useState(null);
  const [showItemTable, setShowItemTable] = useState(false);

  // Redux
  const dispatch = useDispatch();
  const selectedBOM = useSelector(({ bom }) => bom.selectedBOM);
  const selectedBom = selectedBOM && selectedBOM.data.bill_of_material_details;
  const styleList = useSelector(({ style }) => style.styleList);
  const selectedStyleDetails = useSelector(({ style }) => style.selectedStyleDetails);
  const styleDetails = selectedStyleDetails && selectedStyleDetails.data[0];
  const loading = useSelector(({ common }) => common.loading);

  // Router
  const location = useLocation();
  const selectedInventory = location.state;

  // Antd
  const [form] = Form.useForm();
  const { Content } = Layout;
  const { Panel } = Collapse;
  const { Title } = Typography;
  const entry_date_watch = Form.useWatch("entry_date", form);
  const supplier_id_watch = Form.useWatch("supplier_id", form);

  // Others
  const dateFormat = "YYYY-MM-DD";

  // Effects
  useEffect(() => {
    dispatch(
      onGetBuyingOfficeList({
        page: 1,
        per_page: 10,
      }),
    );
    dispatch(onGetStyleList(1, 10));
    form.resetFields();

    return () => {
      // Reset style and BOM redux state on unmount
      dispatch(onResetStyleDetails());
      dispatch(onResetBOM());
    };
  }, [dispatch, form]);

  useEffect(() => {
    const row = location.state?.fabricStoreListRow;
    if (!row?.style_id) return;

    dispatch(onGetStyleDetails(row.style_id));

    if (row.movement_type) {
      setValue(row.movement_type);
      const category = getMovementType(row.movement_type);
      if (category === "incoming") {
        form.setFieldsValue({
          movement_type_incoming: row.movement_type,
          movement_type_outgoing: undefined,
        });
      } else if (category === "outgoing") {
        form.setFieldsValue({
          movement_type_outgoing: row.movement_type,
          movement_type_incoming: undefined,
        });
      }
    }

    form.setFieldsValue({
      style_id: row.style_id,
      entry_date: row.entry_date ? moment(row.entry_date, "YYYY-MM-DD") : undefined,
      requisition_no: row.requisition_no ?? undefined,
      reference_no: row.reference_no ?? undefined,
      supplier_id: row.supplier_id ?? undefined,
      challan_id: row.challan_id ?? undefined,
      invoice_id: row.invoice_id ?? undefined,
    });

    if (row.supplier_id) {
      setSelectedSupplierId(row.supplier_id);
    }
  }, [location.state?.fabricStoreListRow?.id, dispatch, form]);

  const resetStyleDependentState = useCallback(() => {
    form.resetFields([
      "supplier_id",
      "invoice_id",
      "challan_id",
      "reference_no",
      "requisition_no",
      "quantity_ctn",
      "movement_type_incoming",
      "movement_type_outgoing",
    ]);

    setSelectedSupplierId(null);
    setSelectedSupplierType(null);
    setInvoiceList(null);
    setInventoryItems([]);
    setShowItemTable(false);
    setValue(null);
    setEdit(false);
    dispatch(onResetBOM());
  }, [dispatch, form]);

  const onChangeStyle = (value, options) => {
    resetStyleDependentState();
    dispatch(onGetStyleDetails(value));
  };

  const BOMDetails = useCallback(
    (props) => {
      const {
        buying_office_id,
        entry_date,
        movement_type,
        sales_contract_id,
        style_id,
        supplier_id,
        use_fabric_interlining_store,
        sub_contract_factory_id,
      } = props;

      const payload = {
        buying_office_id,
        entry_date,
        movement_type,
        sales_contract_id,
        style_id,
        supplier_id,
        use_fabric_interlining_store,
        sub_contract_factory_id,
      };

      dispatch(onGetBOMDetails(payload));
    },
    [dispatch],
  );

  const onChangeBuyingOfc = (value, options) => {
    dispatch(onGetBuyingOfficeDetails(value));
  };

  const onChangeInternalPO = (value, options) => {
    setOriginalPO(options?.original_po);
    setInternalPO(options?.internal_po);
    form.setFieldValue("original_po", options?.original_po);
  };

  const onChangeOriginalPO = (value, options) => {
    setOriginalPO(options?.original_po);
    setInternalPO(options?.internal_po);
    form.setFieldValue("internal_po", options?.internal_po);
  };

  const getItemDetailsData = useCallback(() => {
    const payload = {
      buyingId: styleDetails?.style?.buying_office_id,
      entryDate: entry_date_watch,
      movementType: value,
      salesContractId: styleDetails?.style?.sales_contract_map_info?.sales_contract_id,
      styleId: styleDetails?.style?.id,
      supplierId: form.getFieldValue("supplier_id"),
    };

    // Check if all keys in payload object has value, if has 0 then it is also considered true
    const allKeyHasValue = Object.values(payload).every((value) => value || value === 0);

    // Call this function and get BOM details (Item Details Table)
    // Only when style, supplier and entry date is changed and all keys in payload object has value
    if (allKeyHasValue) {
      BOMDetails({
        buying_office_id: styleDetails?.style?.buying_office_id,
        entry_date: entry_date_watch,
        movement_type: value,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        style_id: styleDetails?.style?.id,
        supplier_id: form.getFieldValue("supplier_id"),
        use_fabric_interlining_store: "Yes",
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id || null,
      });
    }
  }, [BOMDetails, form, styleDetails, value]);

  useEffect(() => {
    getItemDetailsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [styleDetails?.style?.id, supplier_id_watch, entry_date_watch]);

  useEffect(() => {
    if (styleDetails?.style) {
      form.setFieldsValue({
        buying_office_id: styleDetails?.style?.buying_office_info?.id,
        buying_office_name: styleDetails?.style?.buying_office_info?.name,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        sales_contract_reference_no:
          styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.reference_no,
        buyer_id: styleDetails?.style?.buyer_info?.id,
        buyer_name: styleDetails?.style?.buyer_info?.name,
        brand_id: styleDetails?.style?.brand_info?.id,
        brand_name: styleDetails?.style?.brand_info?.name,
        order_quantity: styleDetails?.style?.active_sales_contract_map_info?.order_quantity
          ? parseInt(styleDetails?.style?.active_sales_contract_map_info?.order_quantity)
          : 0,
      });
    }

    if (styleDetails?.style?.factory_info) {
      form.setFieldsValue({
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id,
        sub_contract_factory_name: styleDetails?.style?.factory_info?.name,
      });
    }
  }, [styleDetails, form]);

  const getInvoiceBySupplier = useCallback(
    async (supplierId) => {
      const query = `${INVOICE_BY_SUPPLIER}/${selectedSupplierId}`;
      const response = await getData(query);

      if (response && response?.data?.code === 200) {
        setInvoiceList(response?.data?.data);
      }
    },
    [selectedSupplierId],
  );

  const getAllChallan = async (filterValues) => {
    const query = `${CHALLAN_LIST}`;
    const bodyData = {
      ...filterValues,
      per_page: 1000,
    };

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setChallanList(response?.data?.data?.data);
    } else {
      message.error("Error getting challan data");
    }
  };

  useEffect(() => {
    // Get challan list on first load
    getAllChallan();
  }, []);

  useEffect(() => {
    // When movement type is receive_from_supplier OR return_to_supplier
    // And supplier id is selected
    if ((value === "receive_from_supplier" || value === "return_to_supplier") && selectedSupplierId) {
      getInvoiceBySupplier(selectedSupplierId);
    }
  }, [value, selectedSupplierId, getInvoiceBySupplier]);

  const onChangeMovement = (selected_movement_type) => {
    if (
      (selected_movement_type === "receive_from_supplier" || selected_movement_type === "return_to_supplier") &&
      !selectedSupplierId
    ) {
      HandleNotification("warning", "bottomRight", "Please select supplier", null);
    }

    setValue(selected_movement_type);
    if (form.getFieldValue("supplier_id")) {
      BOMDetails({
        buying_office_id: styleDetails?.style?.buying_office_id,
        entry_date: entry_date_watch,
        movement_type: selected_movement_type,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        style_id: styleDetails?.style?.id,
        supplier_id: form.getFieldValue("supplier_id"),
        use_fabric_interlining_store: "Yes",
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id || null,
      });
    }
  };

  useEffect(() => {
    const supplierId = form.getFieldValue("supplier_id");
    const entryDate = form.getFieldValue("entry_date");

    const isSupplierRequired = value === "receive_from_supplier" || value === "return_to_supplier";

    if (styleDetails?.style?.id && entryDate && (!isSupplierRequired || supplierId)) {
      BOMDetails({
        buying_office_id: styleDetails?.style?.buying_office_id,
        entry_date: entryDate,
        movement_type: value,
        sales_contract_id: styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
        style_id: styleDetails?.style?.id,
        supplier_id: supplierId || null,
        use_fabric_interlining_store: "Yes",
        sub_contract_factory_id: styleDetails?.style?.factory_info?.id || null,
      });
    }
  }, [
    styleDetails?.style?.id,
    styleDetails?.style?.active_sales_contract_map_info?.active_sales_contract_info?.id,
    styleDetails?.style?.buying_office_id,
    styleDetails?.style?.factory_info?.id,
    form,
    form.getFieldValue("supplier_id"),
    form.getFieldValue("entry_date"),
    value,
    BOMDetails,
  ]);

  const onFinish = (values) => {
    let updatedValues = {};
    updatedValues["movement_type"] = value;
    updatedValues["original_po"] = originalPO;
    updatedValues["internal_po"] = internalPO;

    const styleDetailsInfo = {
      sales_contract_id: styleDetails?.style?.sales_contract_map_info?.sales_contract_id,
      buying_office_id: styleDetails?.style?.buying_office_id,
      style_id: styleDetails?.style?.id,
    };

    updatedValues = {
      ...updatedValues,
      ...values,
      ...styleDetailsInfo,
    };

    // Remove unncessary key values
    delete updatedValues["balance_quantity"];
    delete updatedValues["movement_type_incoming"];
    delete updatedValues["received_total_quantity"];
    delete updatedValues["roll_number"];
    delete updatedValues["store_quantity"];

    dispatch(onCreateInventory(updatedValues));
    setEdit(!onEdit);
  };

  const checkRequiredFields = useCallback(() => {
    const values = form.getFieldsValue();

    if (!values.entry_date) return false;

    if (value === "receive_from_supplier" || value === "return_to_supplier") {
      if (!values.supplier_id) return false;

      if (selectedSupplierType === 0 || selectedSupplierType === 2) {
        if (!values.invoice_id) return false;
      }

      if (selectedSupplierType === 1) {
        if (!values.challan_id) return false;
      }
    }

    if (value?.includes("receive_from_sub_con_factory") && selectedSupplierType === 1) {
      if (!values.challan_id) return false;
    }

    if (value?.includes("issue")) {
      if (!values.requisition_no) return false;
    }

    if (["stock_sale", "send_to_buying_office", "return_to_supplier"].some((val) => value?.includes(val))) {
      if (!values.reference_no) return false;
    }

    if (!values.movement_type_incoming && !values.movement_type_outgoing) {
      return false;
    }

    return true;
  }, [form, value, selectedSupplierType]);

  const handleFormValuesChange = () => {
    setShowItemTable(checkRequiredFields());
  };

  useEffect(() => {
    setShowItemTable(checkRequiredFields());
  }, [checkRequiredFields]);

  const extra = (
    <div className="extra-button-container">
      <Link to="/inventory/store/fabric-store-entry" style={{ marginRight: 8 }}>
        <Button size="small">Back to list</Button>
      </Link>
      <Button size="small" htmlType="submit" onClick={() => form.resetFields()}>
        {" "}
        Reset
      </Button>
    </div>
  );

  return (
    <>
      <InventoryLayOutCommon>
        <AppPageHeader extra={extra} title={<Title level={5}>Fabric Store Entry</Title>} />
        <Content className="item-details">
          <Collapse defaultActiveKey={["1"]} style={{ marginTop: 15 }}>
            <Panel header={<b>Order Details</b>} key="1">
              <Form
                layout="vertical"
                onFinish={onFinish}
                form={form}
                onValuesChange={handleFormValuesChange}
                initialValues={{
                  buyer_name: selectedInventory?.data?.buyer_info?.name || "",
                  brand_name: selectedInventory?.data?.brand_info?.name || "",
                  style_id: selectedInventory?.data?.style_id || "",
                  buying_office_id: selectedInventory?.data?.buying_office_id || "",
                  sales_contract_id: selectedInventory?.data?.sales_contract_id || "",
                  supplier_id: null,
                }}
              >
                <InventoryForm
                  styleDetails={styleDetails}
                  styleList={styleList}
                  onChangeStyle={onChangeStyle}
                  setInventoryItems={setInventoryItems}
                  selectedInventoryDetails={location.state}
                  dateFormat={dateFormat}
                  selectedBOM={selectedInventory ? selectedInventory?.data?.bill_of_material_details : selectedBOM}
                  onChangeBuyingOfc={onChangeBuyingOfc}
                  onChangeInternalPO={onChangeInternalPO}
                  onChangeOriginalPO={onChangeOriginalPO}
                  onChangeMovement={onChangeMovement}
                  originalPO={originalPO}
                  movement={value || ""}
                  form={form}
                  setSelectedSupplierId={setSelectedSupplierId}
                  invoiceList={invoiceList}
                  challanList={challanList}
                  selectedSupplierType={selectedSupplierType}
                  setSelectedSupplierType={setSelectedSupplierType}
                  selectedSupplierId={selectedSupplierId}
                  getAllChallan={getAllChallan}
                  setEdit={setEdit}
                />
              </Form>
            </Panel>
          </Collapse>
          {selectedBom && showItemTable && (
            <ItemTable
              loading={loading}
              setInventoryItems={setInventoryItems}
              selectedBOM={selectedBom}
              movement={value}
              inventoryFormValues={form.getFieldsValue()}
              styleDetails={styleDetails}
              onEdit={onEdit}
              setEdit={setEdit}
              getItemDetailsData={getItemDetailsData}
              form={form}
            />
          )}
        </Content>
      </InventoryLayOutCommon>
    </>
  );
};

export default CreateFabricInventory;
